import { Component, Input, OnInit, Output, EventEmitter, HostListener, ViewChild, ElementRef, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin } from 'rxjs';

import { RecognizedTimeFrameMeta           } from '@shared/factories';
import { NotificationService, ImageService } from '@shared/services';

@Component({
  selector:  'app-photo-item',
  templateUrl: './photo-item.component.html',
  styleUrls:  ['./photo-item.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PhotoItemComponent implements OnChanges {
  @Input() frontPageUrl:     string;
  @Input() backPageUrl:      string;

  @Input() frontPageAngle:   number;
  @Input() backPageAngle:    number;

  @Input() ocrImageWidth:    number;
  @Input() ocrImageHeight:   number;
  @Input() ocrMetaTable:     RecognizedTimeFrameMeta[];
  @Input() disableOcrEvents: boolean;
  @Input() showReset:        boolean;
  @Input() validOCR:         Date;
  @Input() loading:          boolean;

  @Output() imageLoaded      = new EventEmitter<any>();
  @Output() ocrFocus         = new EventEmitter<any>();

  @Output() preselectOCR     = new EventEmitter<void>();
  @Output() resetTimeFrames  = new EventEmitter<void>();

  frontPage:   string;
  backPage:    string;
  image:       string;
  imageError:  string;
  active:      string;
  rendered:    boolean;

  imageStartX: number;
  imageStartY: number;
  mouseStartX: number;
  mouseStartY: number;

  @ViewChild('imageElement')
  imageElement!: ElementRef;

  @HostListener('document:mousedown',  ['$event'])
  @HostListener('document:touchstart', ['$event'])
  moveStartHandler(event): void {
    if (event.target.closest('#ocr-table')) {
      event.preventDefault();
      let el = document.getElementById('ocr-table') as HTMLDivElement;
      if (el) {
        let scale = el.style.transform.includes('scale') ? +el.style.transform.substring(6,7) - 1 : '';
        if (scale) {
          let pos = el.style.transformOrigin.split('px');
          this.imageStartX = +pos[0].trim();
          this.imageStartY = +pos[1].trim();

          this.mouseStartX = this.getMouseX(event);
          this.mouseStartY = this.getMouseY(event);
        }
      }
    }
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  scrollMoveHandler(event): void {
    if (this.imageStartX !== null && this.imageStartX !== undefined) {
      event.preventDefault();
      let el = this.imageElement.nativeElement;;
      let x  = this.mouseStartX - this.getMouseX(event);
      let y  = this.mouseStartY - this.getMouseY(event);

      this.transformHandler({
        x: Math.max(0, Math.min(el.width,  this.imageStartX + x)),
        y: Math.max(0, Math.min(el.height, this.imageStartY + y))
      });
    }
  }

  @HostListener('document:mouseup',  ['$event'])
  @HostListener('document:touchend', ['$event'])
  scrollEndHandler(event: MouseEvent | TouchEvent): void {
    if (this.imageStartX !== null && this.imageStartX !== undefined) {
      event.preventDefault();
      this.imageStartX = null;
      this.imageStartY = null;

      this.mouseStartX = null;
      this.mouseStartY = null;
    }
  }

  constructor(
    private imageService:        ImageService,
    private notificationService: NotificationService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.frontPageUrl?.currentValue && changes?.frontPageUrl?.currentValue !== changes?.frontPageUrl?.previousValue) this.loadImages();
  }

  private resetState(): void {
    this.image      = null;
    this.frontPage  = null;
    this.backPage   = null;
    this.image      = null;
    this.imageError = null;
    this.active     = null;
    this.loading    = true;
  }

  private loadImages(): void {
    this.resetState();
    let calls: any = [];
    if (this.frontPageUrl) calls.push(this.imageService.loadImage(this.frontPageUrl));
    if (this.backPageUrl ) calls.push(this.imageService.loadImage(this.backPageUrl));

    if (calls.length) { 
      forkJoin(calls).subscribe(
        ([frontPage, backPage]: any[]) => {
          this.frontPage = frontPage;
          this.backPage  = backPage;
          this.updateView('frontPage');
        },
        err => this.notificationService.alert(err)
      );
    } else this.loadImageError();
  }

  updateView(side: string): void {
    if (this.active !== side) {
      this.image    = null;
      this.rendered = null;
      this.active   = side;
      if (this[this.active+'Angle']) this.initRotate();
      else                           this.image = this[this.active];
    }
  }

  private updateBase64(base64: string, angle: number = null, side: string = null): void {
    this.imageLoaded.emit({ base64, angle, side: side || this.active });
    setTimeout(() => this.rendered = true);
  }

  imageRendered(): void {
    this.updateBase64(this[this.active], this[this.active+'Angle'], this[this.active]);
  }

  loadImageError(): void {
    this.imageError = 'data:image/png;base64,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';
    this.updateBase64(this.imageError);
  }

  get ocrRatio(): number {
    let wrapper = document.getElementsByClassName('image-wrapper')[0];
    let image   = wrapper && wrapper.getElementsByTagName('img')[0];
    return image && Math.max(this.ocrImageWidth, this.ocrImageHeight) / Math.max(image.width, image.height);
  }

  rotateLeft(e): void {
    this.rotate(-90, e);
  }

  rotateRight(e): void {
    this.rotate(90, e);
  }

  getOcrPosition(polygons: number[]): any {
    let angle = +this.frontPageAngle;
    while (angle < 0) { angle = angle + 360; }
    angle = angle % 360;

    if (angle ===   0) return { left: polygons[0],                     top: polygons[1],                     right: this.ocrImageWidth-polygons[2],  bottom: this.ocrImageHeight-polygons[5]  };
    if (angle ===  90) return { left: this.ocrImageHeight-polygons[7], top: polygons[6],                     right: polygons[3],                     bottom: this.ocrImageWidth - polygons[2] };
    if (angle === 180) return { left: this.ocrImageWidth-polygons[4],  top: this.ocrImageHeight-polygons[5], right: polygons[0],                     bottom: polygons[1]                      };
    if (angle === 270) return { left: polygons[3],                     top: this.ocrImageWidth-polygons[2],  right: this.ocrImageHeight-polygons[7], bottom: polygons[6]                      };
  }

  private initRotate(): void {
    let angleTemp = this[this.active+'Angle'];
    while (angleTemp < 0) { angleTemp = angleTemp + 360; }
    angleTemp = angleTemp % 360;
    this.rotate(angleTemp);
  }

  private rotate(angle: number, e = null): void {
    if (e) e.stopPropagation();
    this.notificationService.wait();
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    document.body.appendChild(canvas);

    var image = new Image();
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = this.image || this[this.active];
    image.onload = () => {
      const imgWidth  = image.naturalWidth;
      const imgHeight = image.naturalHeight;

      const rect    = this.getBoundingRect(imgWidth,imgHeight, angle);
      canvas.width  = rect.width;
      canvas.height = rect.height;

      ctx.translate(canvas.width/2, canvas.height/2);
      ctx.rotate(this.degreeToRadians(angle));
      ctx.drawImage(image, -image.width/2, -image.height/2);

      this.image = canvas.toDataURL();
      if (e) {
        this[this.active+'Angle'] += angle;
        this[this.active+'Angle'] = this[this.active+'Angle']%360;
      }

      this.updateBase64(this.image, this[this.active+'Angle'], this.active);
      this.notificationService.close();
    };
    document.body.removeChild(canvas);
  }

  private getBoundingRect(width: number, height: number, degree: number): any {
    let rad    = this.degreeToRadians(degree);
    let points = [{ x: 0, y: 0 }, { x: width, y: 0}, { x: width, y: height }, { x: 0, y: height }];
    let minX, maxX = 0;
    let minY, maxY = 0;

    for (let i = 0; i < points.length; i++) {
      const point        = points[i];
      const rotatedPoint = this.getRotatedPoint(point.x, point.y, width/2, height/2, rad);

      if (minX == undefined) minX = rotatedPoint.x;
      else                   minX = Math.min(rotatedPoint.x,minX);

      if (minY == undefined) minY = rotatedPoint.y;
      else                   minY = Math.min(rotatedPoint.y,minY);

      maxX = Math.max(rotatedPoint.x,maxX);
      maxY = Math.max(rotatedPoint.y,maxY);
    }

    return {
      x:      minX,
      y:      minY,
      width:  maxX - minX,
      height: maxY - minY
    };
  }

  private degreeToRadians(degree: number): number {
    return degree*Math.PI/180
  }

  private getRotatedPoint(x,y,cx,cy,theta){
    let tempX = x - cx;
    let tempY = y - cy;

    let rotatedX = tempX*Math.cos(theta) - tempY*Math.sin(theta);
    let rotatedY = tempX*Math.sin(theta) + tempY*Math.cos(theta);

    x = rotatedX + cx;
    y = rotatedY + cy;

    return { x, y };
  }

  zoomInPicture(e): void {
    e.stopPropagation();
    let x = e.offsetX;
    let y = e.offsetY;
    this.zoomIn(e.target, x ,y);
  }

  zoomInCenter(e): void {
    e.stopPropagation();
    let el = this.imageElement.nativeElement;
    let x = el.offsetHeight / 2;
    let y = el.offsetWidth / 2;
    this.zoomIn(el, x ,y);
  }

  zoomIn(el, x ,y): void {
    let scale = el.style.transform.includes('scale') ? +el.style.transform.substring(6,7) + 1 : 2;
    this.transformHandler({ scale, x ,y });
  }

  zoomOutPicture(e) {
    e.preventDefault();
    let x = e.offsetX;
    let y = e.offsetY;
    this.zoomOut(e.target, x ,y);
  }

  zoomOutCenter(e): void {
    e.stopPropagation();
    let el = this.imageElement.nativeElement;
    let x = el.offsetHeight / 2;
    let y = el.offsetWidth / 2;
    this.zoomOut(el, x ,y);
  }

  zoomOut(el, x ,y): void {
    let scale = el.style.transform.includes('scale') ? +el.style.transform.substring(6,7) - 1 : '';
    if (!!scale) this.transformHandler({ scale, x ,y });
  }

  private getMouseX(e: MouseEvent | TouchEvent): number {
    return this.getMousePosition(e, 'clientX');
  }

  private getMouseY(e: MouseEvent | TouchEvent): number {
    return this.getMousePosition(e, 'clientY');
  }

  private getMousePosition(e: MouseEvent | TouchEvent, field: string): number {
    return (event as TouchEvent).touches?.length ? (event as TouchEvent).touches[(event as TouchEvent).touches.length-1][field] : (event as MouseEvent)[field];
  }

  private transformHandler({ scale = null, x = null, y = null } = {}): void {
    let elements: HTMLElement[] = [];
    let image = this.imageElement.nativeElement;
    let table = document.getElementById('ocr-table') as HTMLDivElement;

    if (image) elements.push(image);
    if (table) elements.push(table);

    elements.forEach(el => {
      if (scale)  el.style.transform       = `scale(${scale})`;
      if (x && y) el.style.transformOrigin = `${x}px ${y}px`;
    });
  }

  getColor(meta: RecognizedTimeFrameMeta): string {
    return this.OCRConfident(meta) ? 'bg-green-50' : 'bg-red-50';
  }

  OCRConfident(meta: RecognizedTimeFrameMeta): boolean {
    if (meta.confidence*100 > 75) return true;
    return false;
  }

  getPopupStyles(): any {
    let table = document.getElementById('ocr-table') as HTMLDivElement;
    let scale = table.style.transform.includes('scale') ? +table.style.transform.substring(6,7) : '1';

    return ({
      'top': '5px',
      'left': '50%',
      'width': '225px',
      'transform': `translate(-50%, 0) scale(calc(1/${scale}))`,
      'transform-origin': 'top',
      'margin-top': '15px'
    });
  }

}
